import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getApiBrightHorizonsCreditInactivityRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-credit-inactivity-retrieve';
import { useApiBrightHorizonsExitPartnershipDestroy } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-exit-partnership-destroy';
import { getApiCurrentUserRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-current-user-retrieve';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';
import Navigation from '@rover/utilities/navigation';
import setAmplitudeUserProperties from '@rover/utilities/setAmplitudeUserProperties';
import { redirect } from '@rover/utilities/url';

type UseExitPartnershipReturn = {
  isSubmitting: boolean;
  exitHandler: () => Promise<void>;
};

const useExitPartnership = (): UseExitPartnershipReturn => {
  const queryClient = useQueryClient();

  const { mutateAsync: mutateExit } = useApiBrightHorizonsExitPartnershipDestroy({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getApiBrightHorizonsCreditInactivityRetrieveQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getApiCurrentUserRetrieveQueryKey(),
        });
      },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  return {
    isSubmitting,
    exitHandler: useCallback(async () => {
      try {
        setIsSubmitting(true);
        await mutateExit();

        setAmplitudeUserProperties({ is_bright_horizons_user: false });

        setIsSubmitting(false);
        if (isReactNative()) {
          Navigation.openDeepLink('rover://search', true);
        } else {
          redirect('/search');
        }
      } catch (error) {
        setIsSubmitting(false);
        throw error;
      }
    }, [mutateExit]),
  };
};

export default useExitPartnership;
